import styles from  './index.module.scss';
import { type PropType, defineComponent, h, ref, Teleport } from 'vue';
import { utility, viewport_dimensions } from '@phi/components';
import { Timeout } from '@phi/util/timeout';
import { computed } from '@vue/reactivity';
import { px } from '@phi/util/css';
import { clamp } from '@vaaas/fpts/maths';

const WIDTH = 352;

export type TooltipPlacement =
    'top'
    | 'left'
    | 'right'
    | 'bottom'
    | undefined

export const Tooltip = defineComponent({
    props: {
        placement: {
            required: false,
            type: null as any as PropType<TooltipPlacement>,
        },

        timeout: {
            required: false,
            type: Number,
            default: 500,
        },

        shown: {
            required: false,
            type: Boolean,
            default: undefined,
        },
    },

    emits: {
        'update:shown': (x: boolean) => undefined,
    },

    setup(props, { slots, emit }) {
        const container = ref<undefined | HTMLDivElement>(undefined);
        const shown_once = ref(false);
        const fadingOut = ref(true);
        const rect = computed(() => !hidden() && container.value && container.value.getBoundingClientRect());
        const hover_timeout = new Timeout(show_up, props.timeout);
        const leave_timeout = new Timeout(hide_away, props.timeout/4);

        const hidden = (() => {
            if (props.shown === undefined) {
                const inner = ref(true);
                return function hidden(x: boolean | undefined = undefined) {
                    if (x === undefined)
                        return inner.value;
                    else
                        return inner.value = x;
                };
            } else return function hidden(x: boolean | undefined = undefined) {
                if (x === undefined)
                    return !props.shown;
                else
                    return emit('update:shown', !x);
            };
        })();

        function show_up() {
            if (!slots.tooltip)
                return;
            shown_once.value = true;
            hidden(false);
            setTimeout(() => fadingOut.value = false, 50);
        }

        function hide_away() {
            fadingOut.value = true;
            setTimeout(() => hidden(true), 250);
        }

        function on_pointer_enter() {
            hover_timeout.start();
            leave_timeout.stop();
        }

        function on_pointer_leave() {
            hover_timeout.stop();
            leave_timeout.start();
        }

        const hclamp = clamp(0, viewport_dimensions.width - WIDTH/2);

        function style() {
            if (!rect.value) return undefined;
            const r = rect.value;
            switch (props.placement) {
                case 'top':
                    return {
                        bottom: px(viewport_dimensions.height - r.top),
                        left: px(hclamp(r.left - WIDTH/2)),
                    };

                case 'right':
                    return r.top <= viewport_dimensions.height/2
                        ? {
                            top: px(r.top),
                            left: px(r.right),
                        }
                        : {
                            bottom: px(viewport_dimensions.height - r.bottom),
                            left: px(r.right),
                        };

                case 'left':
                    return r.top <= viewport_dimensions.height/2
                        ? {
                            top: px(r.top),
                            right: px(viewport_dimensions.width - r.left),
                        }
                        : {
                            bottom: px(viewport_dimensions.height - r.bottom),
                            right: px(viewport_dimensions.width - r.left),
                        };

                case 'bottom':
                default:
                    return {
                        top: px(r.top + r.height),
                        left: px(hclamp(r.left - WIDTH/2)),
                    };
            }
        }

        return () => h('div', {
            ref: container,
            class: utility.relative,
            onPointerenter: on_pointer_enter,
            onPointerleave: on_pointer_leave,
        }, [
            slots.default && slots.default(),
            (slots.tooltip && shown_once.value)
                ? h(Teleport, { to: '#app' }, h('div', {
                    onPointerenter: on_pointer_enter,
                    onPointerleave: on_pointer_leave,
                    class: {
                        [styles.tooltip]: true,
                        [utility.important_opaque]: fadingOut.value,
                        [utility.important_scaled_down]: fadingOut.value,
                        [utility.small]: true,
                        [utility.absolute]: true,
                        [utility.hidden]: hidden(),
                        [utility.z_maximum]: true,
                        [utility.no_interact]: hidden(),
                    },
                    style: style(),
                }, slots.tooltip()))
                : ''
        ]);
    },
});
